// The screens before the world: sign in, join it, pick or create a character.
import { app, type Screen } from '../app';
import { api, content } from '../api';
import { Widget, TextInput, CharacterIcon, widget, characterIcon, spriteIcon } from '../ui/compose';
import { settings, saveSettings } from '../settings';
import { Backdrop } from '../game/backdrop';
import { GameScreen } from '../game/gameScreen';
import { config, pickWorld, type WorldOption } from '../config';
import { GameConstants } from '../../../shared/src/constants';
import { guestToken } from '../client/stdbLogin';
import { pingWorld } from '../client/worldPing';
import { authConfigured, signIn, signUp, signInGithub, signInGuest, signedInToken, signedInAsGuest, accessToken, signOut } from '../client/supabaseLogin';
import { clientVersion } from '../clientVersion';

interface SessionInfo {
  state: 'none' | 'connecting' | 'characters' | 'game' | 'disconnected';
  world: string; server: string; maxCharacters: number; error: string | null; disconnect: string;
  characters: { name: string; appearance: number; lastPlayed: string }[];
}

/** Screens that sit over the menu backdrop. */
abstract class MenuScreen implements Screen {
  protected root!: Widget;

  enter(): void {
    Backdrop.show();
    this.root = widget('box', { class: 'screen' });
    app.ui.append(this.root);
    this.build();
    this.frame(0);
  }

  protected abstract build(): void;

  frame(dt: number): void {
    Backdrop.update(dt);
  }

  exit(): void { }
}

function field(placeholder: string, value = '', type = 'text', maxLength = 64): TextInput {
  return widget('input', { class: 'field', placeholder, value, type, maxLength, spellcheck: false, autocomplete: 'off' });
}

function button(text: string, onclick: () => void, disabled = false): Widget {
  return widget('button', { class: 'button', onclick: () => { app.audio.play('sfx_ui_click', 0.5, 0); onclick(); }, disabled }, text);
}

/** Gives a widget a test id (window.ashenheart.ui.find), for browser checks. */
function tagged(w: Widget, testId: string): Widget {
  w.testId = testId;
  return w;
}

/** The world the player last chose (config.json `worlds`), else the first. */
function chosenWorld(): WorldOption {
  return pickWorld(config.worlds, settings.world);
}

/** Starts joining a world and shows its progress. */
export function joinWorld(world: WorldOption, token: string | undefined, guest: boolean): void {
  api.startSpacetime(world, token, guest);
  app.show(new ConnectingScreen(world.name));
}

/** How the player got past the login screen, so leaving a world can go back to the world list. */
let login: { guest: boolean } | null = null;

/** After sign-in (or as a guest): the world list, or straight into the only world. */
export function enterWorlds(guest: boolean, token: string | undefined): void {
  login = { guest };
  if (config.worlds.length > 1) { app.show(new WorldScreen(guest)); return; }
  const world = config.worlds[0];
  joinWorld(world, guest ? guestToken(world) : token, guest);
}

/** Leaving a world (or failing to join one): the world list while still signed in (or a guest), else the login screen. */
export function afterWorld(message: string, isError = true): Screen {
  if (login && config.worlds.length > 1 && (login.guest || signedInToken())) return new WorldScreen(login.guest, message, isError);
  login = null;
  return new LoginScreen(message, isError);
}

function pingText(ms: number | null | undefined): [string, string] {
  if (ms === undefined) return ['...', 'dim'];
  if (ms === null) return ['offline', 'error'];
  return [`${ms} ms`, ms < 120 ? 'good' : ms < 250 ? 'hint' : 'error'];
}

// =============================================================================================================

/** Supabase email/password and GitHub sign-in, with optional SpacetimeDB guests. */
export class LoginScreen extends MenuScreen {
  constructor(private message: string | null = null, private isError = true) { super(); }

  protected build(): void {
    const taken = api.takeMessage() as { text: string | null; error: boolean };
    if (taken.text) { this.message = taken.text; this.isError = taken.error; }
    const status = widget('box', { class: `message ${this.isError ? 'error' : 'dim'}` }, this.message ?? '');
    const signed = signedInToken();
    const play = (token: string | undefined, guest: boolean) => enterWorlds(guest, token);
    const panel = widget('box', { class: 'panel', testId: 'spacetime-login', style: { width: '260px', padding: '12px', gap: '6px' } });
    let busy = false;
    const actions: Widget[] = [];
    const run = async (action: () => Promise<void>) => {
      if (busy) return;
      busy = true;
      actions.forEach(w => { w.disabled = true; });
      status.textContent = 'Please wait...';
      status.className = 'message dim';
      try { await action(); }
      catch (e) { status.textContent = (e as Error).message; status.className = 'message error'; }
      finally { busy = false; actions.forEach(w => { w.disabled = false; }); }
    };
    const actionButton = (label: string, action: () => Promise<void>, id: string) => {
      const w = tagged(button(label, () => { void run(action); }), id);
      actions.push(w);
      return w;
    };
    if (signed) {
      // A guest has no way back in once signed out, so that takes a second click.
      const guest = signedInAsGuest();
      let warned = false;
      const signOutButton = actionButton(guest ? 'Leave guest account' : 'Sign out', async () => {
        if (guest && !warned) {
          warned = true;
          signOutButton.textContent = 'Click again: lose this guest';
          status.textContent = 'A guest account cannot be recovered once you leave it, along with its characters.';
          status.className = 'message error';
          return;
        }
        await signOut();
        login = null;
        app.show(new LoginScreen(guest ? 'Left the guest account.' : 'Signed out.', false));
      }, 'auth-signout');
      panel.append(widget('text', { class: 'center strong' }, guest ? 'Welcome back, guest' : 'Welcome back'),
        actionButton('Play', async () => play(await accessToken(), false), 'stdb-play'), signOutButton);
    } else if (authConfigured(config.supabase)) {
      const email = field('Email address', '', 'email', 254);
      email.testId = 'auth-email';
      const password = field('Password', '', 'password', 128);
      password.testId = 'auth-password';
      const submit = async () => {
        const token = await signIn(email.value, password.value);
        password.value = '';
        play(token, false);
      };
      password.addEventListener('keydown', e => { if (e.key === 'Enter') void run(submit); });
      panel.append(widget('text', { class: 'center strong' }, 'Sign in to Ashenheart'),
        widget('text', { class: 'small dim' }, 'Email'), email,
        widget('text', { class: 'small dim' }, 'Password'), password,
        actionButton('Sign in', submit, 'stdb-signin'),
        widget('box', { class: 'row', style: { justifyContent: 'center', gap: '6px' } },
          widget('text', { class: 'small dim' }, 'New here?'),
          tagged(button('Create an account', () => { if (!busy) app.show(new RegisterScreen(email.value)); }), 'auth-register')),
        widget('box', { class: 'row', style: { gap: '8px', paddingTop: '3px' } },
          widget('box', { class: 'rule grow' }), widget('text', { class: 'small dim' }, 'or'), widget('box', { class: 'rule grow' })));
      const social = widget('box', { class: 'row', style: { justifyContent: 'center', gap: '10px' } });
      for (const provider of ['github', 'google', 'microsoft']) {
        const enabled = provider === 'github';
        const w = widget('button', { class: enabled ? 'button' : 'button disabled',
          testId: `auth-${provider}`, disabled: !enabled,
          title: enabled ? 'Sign in with GitHub' : `${provider === 'google' ? 'Google' : 'Microsoft'} — coming soon`,
          style: { width: '34px', height: '28px', opacity: enabled ? '1' : '0.35', filter: enabled ? '' : 'grayscale(1)' },
          onclick: () => { if (enabled) void run(signInGithub); },
        }, spriteIcon(`auth_${provider}`, 16));
        if (enabled) actions.push(w);
        social.append(w);
      }
      panel.append(social, widget('text', { class: 'center small dim' }, 'Google & Microsoft coming soon'));
    }
    // Guests are Supabase anonymous users (one per browser, every world). Without that, local development worlds may
    // allow SpacetimeDB's own guests; the world list then offers only those worlds.
    if (!signed && authConfigured(config.supabase) && config.supabase.guests)
      panel.append(actionButton('Play as guest', async () => play(await signInGuest(), false), 'auth-guest'));
    else if (!signed && config.worlds.some(w => w.guests)) panel.append(tagged(button('Play as guest', () => {
      if (!busy) play(undefined, true);
    }), 'stdb-guest'));
    if (!signed && !authConfigured(config.supabase) && !config.worlds.some(w => w.guests))
      status.textContent = 'Sign-in is not configured: set supabase.projectId and supabase.publishableKey in config.json.';
    this.root.append(widget('box', { class: 'menu' }, widget('box', { class: 'menu-title' }, 'Ashenheart'), panel, status),
      widget('box', { class: 'row', style: { position: 'absolute', right: '8px', bottom: '8px', gap: '8px' } },
        widget('text', { class: 'small dim', testId: 'client-version' }, `Client ${clientVersion}`),
        widget('text', { class: 'small dim', testId: 'client-protocol' }, `Protocol ${GameConstants.protocolVersion}`)));
  }
}

// =============================================================================================================

/** Supabase email/password sign-up, on its own screen so it is not mistaken for signing in. */
export class RegisterScreen extends MenuScreen {
  constructor(private email = '') { super(); }

  protected build(): void {
    const status = widget('box', { class: 'message dim' }, '');
    const email = field('Email address', this.email.trim(), 'email', 254);
    email.testId = 'register-email';
    const password = field('Password', '', 'password', 128);
    password.testId = 'register-password';
    const confirm = field('Repeat password', '', 'password', 128);
    confirm.testId = 'register-confirm';
    let busy = false;
    const create = tagged(button('Create account', () => { void submit(); }), 'auth-signup');
    const back = tagged(button('Back', () => { if (!busy) app.show(new LoginScreen()); }), 'register-back');
    const fail = (text: string) => { status.textContent = text; status.className = 'message error'; };
    const submit = async () => {
      if (busy) return;
      if (!email.value.trim()) return fail('Enter your email address.');
      if (password.value.length < 6) return fail('Choose a password of at least 6 characters.');
      if (password.value !== confirm.value) return fail('The passwords do not match.');
      busy = true;
      create.disabled = back.disabled = true;
      status.textContent = 'Please wait...';
      status.className = 'message dim';
      try {
        const token = await signUp(email.value, password.value);
        if (token) enterWorlds(false, token);
        else app.show(new LoginScreen('Check your email to confirm your account, then sign in.', false));
      } catch (e) { fail((e as Error).message); }
      finally { busy = false; create.disabled = back.disabled = false; password.value = confirm.value = ''; }
    };
    confirm.addEventListener('keydown', e => { if (e.key === 'Enter') void submit(); });
    const panel = widget('box', { class: 'panel', testId: 'register', style: { width: '260px', padding: '12px', gap: '6px' } },
      widget('text', { class: 'center strong' }, 'Create your account'),
      widget('text', { class: 'small dim' }, 'Email'), email,
      widget('text', { class: 'small dim' }, 'Password'), password,
      widget('text', { class: 'small dim' }, 'Repeat password'), confirm,
      widget('box', { class: 'buttons' }, create, back));
    this.root.append(widget('box', { class: 'menu' }, widget('box', { class: 'menu-title' }, 'Ashenheart'), panel, status));
  }
}

// =============================================================================================================

/** After sign-in: the worlds (each its own characters), with each server's ping; the choice is remembered in settings. */
export class WorldScreen extends MenuScreen {
  private rows: { world: WorldOption; row: Widget }[] = [];
  private join!: Widget;
  private status!: Widget;
  private busy = false;

  constructor(private guest: boolean, private message: string | null = null, private isError = true) { super(); }

  /** Guests can only join worlds that allow them. */
  private open(w: WorldOption): boolean { return !this.guest || w.guests; }

  private get chosen(): WorldOption | undefined {
    const c = chosenWorld();
    return this.open(c) ? c : config.worlds.find(w => this.open(w));
  }

  protected build(): void {
    const taken = api.takeMessage() as { text: string | null; error: boolean };
    if (taken.text) { this.message = taken.text; this.isError = taken.error; }
    const list = widget('box', { class: 'list', testId: 'world-picker' });
    this.rows = config.worlds.map(world => {
      const open = this.open(world);
      const ping = widget('text', { class: 'small dim' }, '...');
      const row = widget('box', { class: 'list-row', testId: `world-${world.id}`,
        onclick: () => { if (open) this.choose(world); },
        ondblclick: () => { if (open) { this.choose(world); this.joinChosen(); } },
      }, widget('box', { class: 'col grow', style: { gap: '1px' } }, widget('text', { class: open ? '' : 'dim' }, world.name),
        ...(open ? [] : [widget('text', { class: 'small dim' }, 'Sign in to play here')])), ping);
      void pingWorld(world.uri).then(ms => {
        const [text, tone] = pingText(ms);
        ping.textContent = text;
        ping.className = `small ${tone}`;
      });
      return { world, row };
    });
    list.append(...this.rows.map(r => r.row));
    this.join = tagged(button('Join', () => this.joinChosen()), 'world-join');
    this.status = widget('box', { class: `message ${this.isError ? 'error' : 'dim'}` }, this.message ?? '');
    this.root.append(widget('box', { class: 'menu' }, widget('box', { class: 'menu-title' }, 'Choose a world'),
      widget('box', { class: 'panel', style: { width: '260px', padding: '10px 12px', gap: '6px' } }, list,
        widget('box', { class: 'buttons' }, this.join, tagged(button('Back', () => this.back()), 'world-back'))),
      this.status));
    this.mark();
    window.addEventListener('keydown', this.onKey);
  }

  private onKey = (e: KeyboardEvent) => {
    const open = config.worlds.filter(w => this.open(w));
    const at = open.indexOf(this.chosen!);
    if (e.key === 'ArrowUp' && at > 0) this.choose(open[at - 1]);
    else if (e.key === 'ArrowDown' && at < open.length - 1) this.choose(open[at + 1]);
    else if (e.key === 'Enter') this.joinChosen();
    else if (e.key === 'Escape') this.back();
  };

  exit(): void {
    window.removeEventListener('keydown', this.onKey);
    super.exit();
  }

  private choose(world: WorldOption): void {
    settings.world = world.id;
    saveSettings();
    this.mark();
  }

  private mark(): void {
    const chosen = this.chosen;
    for (const { world, row } of this.rows) row.className = `list-row${world === chosen ? ' selected' : ''}`;
    this.join.disabled = !chosen || this.busy;
  }

  private back(): void {
    login = null;
    app.show(new LoginScreen());
  }

  private joinChosen(): void {
    const world = this.chosen;
    if (!world || this.busy) return;
    if (this.guest) { joinWorld(world, guestToken(world), true); return; }
    this.busy = true;
    this.mark();
    this.status.textContent = 'Please wait...';
    this.status.className = 'message dim';
    accessToken().then(token => joinWorld(world, token, false), e => {
      this.busy = false;
      this.mark();
      this.status.textContent = (e as Error).message;
      this.status.className = 'message error';
    });
  }
}

// =============================================================================================================

export class ConnectingScreen extends MenuScreen {
  private label!: Widget;
  private dots = 0;

  constructor(private worldName: string) { super(); }

  protected build(): void {
    this.label = widget('box', null, `Joining ${this.worldName}`);
    this.root.append(widget('box', { class: 'menu' }, widget('box', { class: 'panel center' }, this.label,
      button('Cancel', () => { api.leaveWorld(null, false); app.show(afterWorld('Cancelled.')); }))));
  }

  frame(dt: number): void {
    super.frame(dt);
    api.pump(dt);
    this.dots += dt * 3;
    this.label.textContent = `Joining ${this.worldName}${'.'.repeat(Math.floor(this.dots) % 4)}`;
    const s = api.sessionState() as SessionInfo;
    if (s.state === 'characters') app.show(s.characters.length === 0 ? new CreateScreen() : new CharactersScreen());
    else if (s.state === 'game') app.show(new GameScreen());
    else if (s.state === 'disconnected') {
      const message = s.disconnect;
      api.leaveWorld(null, false);
      app.show(afterWorld(message));
    }
  }
}

/** Leaving character select or creation (or losing the world there) goes back to the world list (or login screen). */
function checkSession(): SessionInfo | null {
  const s = api.sessionState() as SessionInfo;
  if (s.state === 'game') { app.show(new GameScreen()); return null; }
  if (s.state === 'disconnected' || s.state === 'none') {
    const message = s.disconnect || 'Disconnected.';
    api.leaveWorld(null, false);
    api.logout(null);
    app.show(afterWorld(message));
    return null;
  }
  return s;
}

function disconnect(): void {
  api.leaveWorld(null, false);
  api.logout(null);
  app.show(afterWorld('Disconnected.', false));
}

// =============================================================================================================

export class CharactersScreen extends MenuScreen {
  private list!: Widget;
  private error!: Widget;
  private title!: Widget;
  private play!: Widget;
  private create!: Widget;
  private selected = 0;
  private shownKey = '';
  private state: SessionInfo | null = null;

  protected build(): void {
    this.list = widget('box', { class: 'list' });
    this.error = widget('box', { class: 'message error' });
    this.title = widget('box', { class: 'menu-title' });
    this.play = button('Play', () => this.choose());
    this.create = button('Create new', () => app.show(new CreateScreen()));
    this.root.append(widget('box', { class: 'menu' }, this.title,
      widget('box', { class: 'panel', style: { minWidth: `250px` } }, widget('box', { class: 'dim' }, 'Choose a character'), this.list,
        widget('box', { class: 'buttons' }, this.play, this.create, button('Disconnect', disconnect))),
      this.error));
    window.addEventListener('keydown', this.onKey);
  }

  private onKey = (e: KeyboardEvent) => {
    const n = this.state?.characters.length ?? 0;
    if (e.key === 'ArrowUp') this.selected = Math.max(0, this.selected - 1);
    else if (e.key === 'ArrowDown') this.selected = Math.min(n - 1, this.selected + 1);
    else if (e.key === 'Enter') this.choose();
    else if (e.key === 'Escape') disconnect();
    else return;
    this.shownKey = '';
  };

  exit(): void {
    window.removeEventListener('keydown', this.onKey);
    super.exit();
  }

  private choose(): void {
    const c = this.state?.characters[this.selected];
    if (c) api.selectCharacter(c.name);
  }

  frame(dt: number): void {
    super.frame(dt);
    api.pump(dt);
    const s = checkSession();
    if (!s) return;
    this.state = s;
    this.selected = Math.max(0, Math.min(this.selected, s.characters.length - 1));
    this.title.textContent = s.server;
    this.error.textContent = s.error ?? '';
    this.play.disabled = s.characters.length === 0;
    this.create.disabled = s.characters.length >= s.maxCharacters;
    const key = JSON.stringify([s.characters, this.selected]);
    if (key === this.shownKey) return;
    this.shownKey = key;
    this.list.replaceChildren(...s.characters.map((c, i) => widget('box', {
      class: `list-row${i === this.selected ? ' selected' : ''}`,
      onclick: () => { this.selected = i; this.shownKey = ''; },
      ondblclick: () => { this.selected = i; this.choose(); },
    }, characterIcon(c.appearance, 18), widget('box', { class: 'col', style: { gap: `2px` } },
      widget('box', null, c.name), widget('box', { class: 'small dim' }, `Last played ${c.lastPlayed}`)))));
    if (s.characters.length === 0) this.list.append(widget('box', { class: 'dim center' }, 'No characters yet'));
  }
}

// =============================================================================================================

export class CreateScreen extends MenuScreen {
  private appearance = Math.max(0, Math.min(content.appearances - 1, settings.appearance));
  private preview!: CharacterIcon;
  private name!: TextInput;
  private hint!: Widget;
  private error!: Widget;
  private createButton!: Widget;
  private waiting = false;
  private anim = 0;

  protected build(): void {
    this.preview = characterIcon(this.appearance, 36);
    this.name = field('Name', '', 'text', content.maxNameLength);
    this.name.editor.filter = c => /[A-Za-z]/.test(c);
    this.name.addEventListener('keydown', e => { if (e.key === 'Enter') this.submit(); if (e.key === 'Escape') this.back(); });
    this.hint = widget('box', { class: 'small dim' });
    this.error = widget('box', { class: 'message error' });
    this.createButton = button('Create', () => this.submit());
    const look = (d: number) => { this.appearance = (this.appearance + d + content.appearances) % content.appearances; };
    this.root.append(widget('box', { class: 'menu' }, widget('box', { class: 'menu-title' }, 'Create a character'),
      widget('box', { class: 'panel' },
        widget('box', { class: 'row', style: { justifyContent: 'center' } }, button('<', () => look(-1)), this.preview, button('>', () => look(1))),
        widget('text', null, 'Name'), this.name, this.hint,
        widget('box', { class: 'buttons' }, this.createButton, button('Back', () => this.back()))),
      this.error));
    this.name.focus();
  }

  private back(): void {
    const s = api.sessionState() as SessionInfo;
    if (s.characters.length > 0) app.show(new CharactersScreen());
    else disconnect();
  }

  private submit(): void {
    const check = api.checkName(this.name.value) as { name: string | null; error: string | null };
    if (this.waiting || check.error || !check.name) return;
    this.waiting = true;
    settings.appearance = this.appearance;
    saveSettings();
    api.createCharacter(this.name.value, this.appearance);
  }

  frame(dt: number): void {
    super.frame(dt);
    api.pump(dt);
    const s = checkSession();
    if (!s) return;
    if (s.error) this.waiting = false;
    this.error.textContent = s.error ?? '';
    this.anim += dt;
    const frame = [1, 0, 2, 0][Math.floor(this.anim * 6) % 4], dir = Math.floor(this.anim / 2) % 4;
    this.preview.setAppearance(this.appearance, dir, frame);
    const check = api.checkName(this.name.value) as { name: string | null; error: string | null };
    this.hint.textContent = check.error ?? (check.name ? `You will be known as ${check.name}` : '3-16 letters (A-Z), no spaces or numbers');
    this.hint.className = `small ${check.error ? 'error' : check.name ? 'good' : 'dim'}`;
    this.createButton.disabled = !!check.error || !check.name || this.waiting;
    this.createButton.textContent = this.waiting ? 'Creating...' : 'Create';
  }
}
